<template>
  <NuxtLink v-if="to" v-slot="{ href: resolvedHref, navigate }" :to="to" custom>
    <Link
      :size="mappedSize"
      :variant="variant"
      :state="state"
      :underline="underline"
      :show-icon="showIcon"
      :icon="icon"
      :href="resolvedHref"
      :target="target"
      v-bind="$attrs"
      @click="
        (e: MouseEvent) => {
          $emit('click', e)
          navigate(e)
        }
      "
    >
      <slot>{{ label }}</slot>
    </Link>
  </NuxtLink>
  <Link
    v-else
    :size="mappedSize"
    :variant="variant"
    :state="state"
    :underline="underline"
    :show-icon="showIcon"
    :icon="icon"
    :href="href ?? undefined"
    :target="target"
    v-bind="$attrs"
    @click="(e: MouseEvent) => $emit('click', e)"
  >
    <slot>{{ label }}</slot>
  </Link>
</template>

<script lang="ts" setup>
import type { Component } from 'vue'
import { Link } from '@p-utilities/vue'
import type { LinkSize, LinkVariant, LinkState } from '@p-utilities/vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'md',
    validator: (val: string) => ['sm', 'md', 'small', 'medium'].includes(val),
  },
  variant: {
    type: String as () => LinkVariant,
    default: 'default',
  },
  state: {
    type: String as () => LinkState,
    default: 'default',
    validator: (val: string) => ['default', 'pressed'].includes(val),
  },
  underline: {
    type: Boolean,
    default: false,
  },
  showIcon: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: Object as () => Component,
    default: undefined,
  },
  href: {
    type: String,
    default: null,
  },
  to: {
    type: [String, Object],
    default: null,
  },
  target: {
    type: String,
    default: '_self',
    validator: (val: string) => ['_self', '_blank'].includes(val),
  },
})

defineEmits<{ click: [e: MouseEvent] }>()

const sizeMap: Record<string, LinkSize> = {
  sm: 'small',
  md: 'medium',
  small: 'small',
  medium: 'medium',
}

const mappedSize = computed<LinkSize>(() => sizeMap[props.size] ?? 'medium')
</script>
